import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { WhatsAppButton } from '../../components/feature/WhatsAppButton';
import { ListingCard } from '../../components/base/ListingCard';
import { Button } from '../../components/base/Button';
import { mockListings } from '../../mocks/listings';

const ListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const listing = mockListings.find(l => l.id === parseInt(id || '0'));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  if (!listing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0A1628] mb-4">İlan Bulunamadı</h1>
          <Link to="/ilanlar" className="text-[#D4AF37] hover:underline">
            İlanlara Dön
          </Link>
        </div>
      </div>
    );
  }

  const similarListings = mockListings
    .filter(l => l.id !== listing.id && l.location === listing.location)
    .slice(0, 3);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('listing', listing.facility_name);

      const response = await fetch('https://readdy.ai/api/form/d68s2evmvg9ih2c79nl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setShowContactForm(false);
          setFormSubmitted(false);
          setFormData({ name: '', phone: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = `Merhaba, ${listing.facility_name} ilanınız hakkında bilgi almak istiyorum.`;
    window.open(`https://wa.me/${listing.contact_whatsapp.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${listing.contact_phone}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-3 text-[#6B7A90] mb-8 text-sm">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/ilanlar" className="hover:text-[#D4AF37] transition-colors">İlanlar</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#0A1628]">{listing.facility_name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-2">
              {/* Title & Key Info */}
              <h1 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                {listing.facility_name} – Devremülk İlanı
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 rounded-full">
                  <i className="ri-calendar-line text-[#D4AF37]"></i>
                  <span className="text-sm font-semibold text-[#0A1628]">
                    {listing.week_count} Hafta
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FC] rounded-full">
                  <i className="ri-map-pin-line text-[#6B7A90]"></i>
                  <span className="text-sm text-[#0A1628]">{listing.location}</span>
                </div>
                {listing.season && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FC] rounded-full">
                    <i className="ri-sun-line text-[#6B7A90]"></i>
                    <span className="text-sm text-[#0A1628]">{listing.season}</span>
                  </div>
                )}
              </div>

              {/* Image Gallery */}
              <div className="mb-8">
                <div className="relative h-[500px] rounded-2xl overflow-hidden mb-4">
                  <img
                    src={listing.images[currentImageIndex]}
                    alt={`${listing.facility_name} - Görsel ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(prev => prev === 0 ? listing.images.length - 1 : prev - 1)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line text-2xl text-[#0A1628]"></i>
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(prev => prev === listing.images.length - 1 ? 0 : prev + 1)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line text-2xl text-[#0A1628]"></i>
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  <div className="absolute bottom-4 right-4 px-4 py-2 bg-black/70 backdrop-blur-sm rounded-full">
                    <span className="text-sm text-white">
                      {currentImageIndex + 1} / {listing.images.length}
                    </span>
                  </div>
                </div>

                {/* Thumbnails */}
                <div className="grid grid-cols-6 gap-2">
                  {listing.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-20 rounded-lg overflow-hidden cursor-pointer transition-all ${
                        currentImageIndex === index
                          ? 'ring-4 ring-[#D4AF37]'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl border border-[#E5E9F0] p-8 mb-8">
                <h2 className="text-2xl font-bold text-[#0A1628] mb-4">
                  Açıklama
                </h2>
                <p className="text-base text-[#6B7A90] leading-relaxed whitespace-pre-line">
                  {listing.description}
                </p>
              </div>

              {/* Amenities */}
              {listing.amenities && listing.amenities.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#E5E9F0] p-8 mb-8">
                  <h2 className="text-2xl font-bold text-[#0A1628] mb-6">
                    Tesis Özellikleri
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {listing.amenities.map((amenity: { icon: string; name: string; description: string }, index: number) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 p-4 bg-[#F7F9FC] rounded-xl hover:bg-[#D4AF37]/5 transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/10 rounded-lg flex-shrink-0">
                          <i className={`${amenity.icon} text-xl text-[#D4AF37]`}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-[#0A1628] mb-0.5">
                            {amenity.name}
                          </h4>
                          <p className="text-xs text-[#6B7A90]">
                            {amenity.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activities */}
              {listing.activities && listing.activities.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#E5E9F0] p-8 mb-8">
                  <h2 className="text-2xl font-bold text-[#0A1628] mb-6">
                    Aktiviteler
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    {listing.activities.map((activity: { icon: string; name: string; description: string }, index: number) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 p-4 bg-gradient-to-br from-[#D4AF37]/5 to-transparent rounded-xl border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] rounded-lg flex-shrink-0">
                          <i className={`${activity.icon} text-xl text-white`}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-[#0A1628] mb-0.5">
                            {activity.name}
                          </h4>
                          <p className="text-xs text-[#6B7A90]">
                            {activity.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="bg-white rounded-2xl border border-[#E5E9F0] p-8">
                <h2 className="text-2xl font-bold text-[#0A1628] mb-6">
                  Detaylar
                </h2>
                <div className="grid grid-cols-2 gap-6">
                  {listing.createdAt && (
                    <div>
                      <p className="text-sm text-[#6B7A90] mb-1">İlan Tarihi</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#D4AF37]/10 rounded-full">
                          <i className="ri-calendar-line text-[#D4AF37]"></i>
                        </div>
                        <p className="text-base font-semibold text-[#0A1628]">
                          {new Date(listing.createdAt).toLocaleDateString('tr-TR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {listing.owner_name && (
                    <div>
                      <p className="text-sm text-[#6B7A90] mb-1">İlan Sahibi</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#D4AF37]/10 rounded-full">
                          <i className="ri-user-line text-[#D4AF37]"></i>
                        </div>
                        <p className="text-base font-semibold text-[#0A1628]">
                          {listing.owner_name}
                        </p>
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-[#6B7A90] mb-1">Tesis</p>
                    <p className="text-base font-semibold text-[#0A1628]">
                      {listing.facility_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7A90] mb-1">Lokasyon</p>
                    <p className="text-base font-semibold text-[#0A1628]">
                      {listing.location}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7A90] mb-1">Hafta Sayısı</p>
                    <p className="text-base font-semibold text-[#0A1628]">
                      {listing.week_count} Hafta
                    </p>
                  </div>
                  {listing.season && (
                    <div>
                      <p className="text-sm text-[#6B7A90] mb-1">Sezon</p>
                      <p className="text-base font-semibold text-[#0A1628]">
                        {listing.season}
                      </p>
                    </div>
                  )}
                  {listing.unit_type && (
                    <div>
                      <p className="text-sm text-[#6B7A90] mb-1">Oda Tipi</p>
                      <p className="text-base font-semibold text-[#0A1628]">
                        {listing.unit_type}
                      </p>
                    </div>
                  )}
                  {listing.capacity && (
                    <div>
                      <p className="text-sm text-[#6B7A90] mb-1">Kapasite</p>
                      <p className="text-base font-semibold text-[#0A1628]">
                        {listing.capacity} Kişi
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Contact Panel */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <div className="bg-white rounded-2xl border-2 border-[#D4AF37] p-8 mb-6">
                  {listing.price && (
                    <div className="mb-6 pb-6 border-b border-[#E5E9F0]">
                      <p className="text-sm text-[#6B7A90] mb-1">Fiyat</p>
                      <p className="text-4xl font-bold text-[#0A1628]">
                        ₺{listing.price.toLocaleString('tr-TR')}
                      </p>
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-[#0A1628] mb-6">
                    İletişime Geçin
                  </h3>

                  <div className="space-y-3">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={handleWhatsApp}
                    >
                      <i className="ri-whatsapp-line mr-2"></i>
                      WhatsApp ile İletişim
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      onClick={handleCall}
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Telefonla Ara
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      onClick={() => setShowContactForm(!showContactForm)}
                    >
                      <i className="ri-mail-line mr-2"></i>
                      Mesaj Gönder
                    </Button>
                  </div>

                  {/* Contact Form */}
                  {showContactForm && (
                    <div className="mt-6 pt-6 border-t border-[#E5E9F0]">
                      {formSubmitted ? (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                            <i className="ri-check-line text-3xl text-green-600"></i>
                          </div>
                          <p className="text-base font-semibold text-green-600">
                            Mesajınız gönderildi!
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleFormSubmit} className="space-y-4" data-readdy-form id="listing-contact-form">
                          <div>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="Adınız Soyadınız"
                              className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                            />
                          </div>
                          <div>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              required
                              placeholder="Telefon"
                              className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                            />
                          </div>
                          <div>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              required
                              rows={4}
                              maxLength={500}
                              placeholder="Mesajınız (max 500 karakter)"
                              className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                            />
                          </div>
                          <Button 
                            type="submit" 
                            variant="primary" 
                            size="md" 
                            className="w-full"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                          </Button>
                        </form>
                      )}
                    </div>
                  )}
                </div>

                {/* Trust Badge */}
                <div className="bg-[#F7F9FC] rounded-2xl p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-[#0A1628] mb-1">
                        Güvenli İşlem
                      </h4>
                      <p className="text-sm text-[#6B7A90]">
                        Tüm ilanlar doğrulanmıştır
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-customer-service-2-line text-xl text-[#D4AF37]"></i>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-[#0A1628] mb-1">
                        Destek
                      </h4>
                      <p className="text-sm text-[#6B7A90]">
                        7/24 müşteri desteği
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Listings */}
          {similarListings.length > 0 && (
            <section className="mt-24">
              <h2 className="text-3xl font-bold text-[#0A1628] mb-8">
                Benzer İlanlar
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {similarListings.map((listing) => (
                  <ListingCard key={listing.id} {...listing} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ListingDetailPage;